// Admin API helper with JWT auth.
const API = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = "arabell_admin_jwt";

export const getToken = () => localStorage.getItem(TOKEN_KEY);
export const setToken = (t) => localStorage.setItem(TOKEN_KEY, t);
export const clearToken = () => localStorage.removeItem(TOKEN_KEY);

const headers = () => {
  const t = getToken();
  return {
    "Content-Type": "application/json",
    ...(t ? { Authorization: `Bearer ${t}` } : {}),
  };
};

const handle = async (res) => {
  if (res.status === 204) return null;
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const d = data.detail;
    const msg = typeof d === "string" ? d : (d && d.message) || `Errore (${res.status})`;
    const err = new Error(msg);
    err.status = res.status;
    err.code = d && typeof d === "object" ? d.code : undefined;
    throw err;
  }
  return data;
};

export const adminApi = {
  login: (username, password) =>
    fetch(`${API}/api/auth/admin/login`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ username, password }),
    }).then(handle),

  me: () => fetch(`${API}/api/auth/admin/me`, { headers: headers() }).then(handle),

  // 2FA — Google Authenticator
  twofaEnrollConfirm: (pending_token, code) =>
    fetch(`${API}/api/auth/admin/2fa/enroll-confirm`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ pending_token, code }),
    }).then(handle),
  twofaVerify: (pending_token, code, is_backup = false) =>
    fetch(`${API}/api/auth/admin/2fa/verify`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ pending_token, code, is_backup }),
    }).then(handle),
  twofaRecoveryRequest: (pending_token) =>
    fetch(`${API}/api/auth/admin/2fa/recovery/request`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ pending_token }),
    }).then(handle),
  twofaRecoveryVerify: (pending_token, otp) =>
    fetch(`${API}/api/auth/admin/2fa/recovery/verify`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ pending_token, otp }),
    }).then(handle),
  twofaStatus: () =>
    fetch(`${API}/api/auth/admin/2fa/status`, { headers: headers() }).then(handle),
  twofaRegenerateBackup: (password, code) =>
    fetch(`${API}/api/auth/admin/2fa/regenerate-backup-codes`, {
      method: "POST", headers: headers(),
      body: JSON.stringify({ password, code }),
    }).then(handle),
  revokeAllSessions: () =>
    fetch(`${API}/api/auth/admin/2fa/sessions/revoke-all`, {
      method: "POST", headers: headers(),
    }).then(handle),
  cardosDiagnostics: () =>
    fetch(`${API}/api/admin/cardos/diagnostics`, { headers: headers() }).then(handle),
  cardosRaw: (kind) =>
    fetch(`${API}/api/admin/cardos/raw/${kind}`, { headers: headers() }).then(handle),
  cardosWebhookSetup: () =>
    fetch(`${API}/api/admin/cardos/webhook/setup`, { method: "POST", headers: headers() }).then(handle),
  bankingGet: () =>
    fetch(`${API}/api/admin/banking`, { headers: headers() }).then(handle),
  bankingUpdate: (data) =>
    fetch(`${API}/api/admin/banking`, {
      method: "PUT", headers: headers(),
      body: JSON.stringify(data),
    }).then(handle),

  // Clients
  listClients: (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return fetch(`${API}/api/admin/clients${qs ? `?${qs}` : ""}`, { headers: headers() }).then(handle);
  },
  createClient: (data) =>
    fetch(`${API}/api/admin/clients`, { method: "POST", headers: headers(), body: JSON.stringify(data) }).then(handle),
  updateClient: (id, patch) =>
    fetch(`${API}/api/admin/clients/${id}`, { method: "PATCH", headers: headers(), body: JSON.stringify(patch) }).then(handle),
  deleteClient: (id) =>
    fetch(`${API}/api/admin/clients/${id}`, { method: "DELETE", headers: headers() }).then(handle),
  walletHistory: (id) =>
    fetch(`${API}/api/admin/clients/${id}/wallet`, { headers: headers() }).then(handle),
  walletAction: (id, data) =>
    fetch(`${API}/api/admin/clients/${id}/wallet`, { method: "POST", headers: headers(), body: JSON.stringify(data) }).then(handle),
  walletBackfill: () =>
    fetch(`${API}/api/admin/clients/wallet/backfill`, { method: "POST", headers: headers() }).then(handle),
  resetClientPassword: (id) =>
    fetch(`${API}/api/admin/clients/${id}/reset-password`, { method: "POST", headers: headers() }).then(handle),
  // Client documents (anagrafica: CI, tessera sanitaria, patente, visura, ecc.)
  listClientDocuments: (id) =>
    fetch(`${API}/api/admin/clients/${id}/documents`, { headers: headers() }).then(handle),
  addClientDocument: (id, data) =>
    fetch(`${API}/api/admin/clients/${id}/documents`, { method: "POST", headers: headers(), body: JSON.stringify(data) }).then(handle),
  deleteClientDocument: (id, docId) =>
    fetch(`${API}/api/admin/clients/${id}/documents/${docId}`, { method: "DELETE", headers: headers() }).then(handle),

  // Collaborators
  listCollaborators: (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return fetch(`${API}/api/admin/collaborators${qs ? `?${qs}` : ""}`, { headers: headers() }).then(handle);
  },
  createCollaborator: (data) =>
    fetch(`${API}/api/admin/collaborators`, { method: "POST", headers: headers(), body: JSON.stringify(data) }).then(handle),
  updateCollaborator: (id, patch) =>
    fetch(`${API}/api/admin/collaborators/${id}`, { method: "PATCH", headers: headers(), body: JSON.stringify(patch) }).then(handle),
  resetCollaboratorPassword: (id) =>
    fetch(`${API}/api/admin/collaborators/${id}/reset-password`, { method: "POST", headers: headers() }).then(handle),
  deleteCollaborator: (id) =>
    fetch(`${API}/api/admin/collaborators/${id}`, { method: "DELETE", headers: headers() }).then(handle),

  // Reviews & Quotes legacy (still token-based for now, but expose via JWT)
  listPendingReviews: () =>
    fetch(`${API}/api/admin/pending-reviews`, { headers: headers() }).then(handle),
  approveReview: (id) =>
    fetch(`${API}/api/admin/pending-reviews/${id}/approve`, { method: "POST", headers: headers() }).then(handle),
  rejectReview: (id) =>
    fetch(`${API}/api/admin/pending-reviews/${id}/reject`, { method: "POST", headers: headers() }).then(handle),
  listQuotes: () =>
    fetch(`${API}/api/admin/quotes`, { headers: headers() }).then(handle),
  setQuoteStatus: (id, newStatus) =>
    fetch(`${API}/api/admin/quotes/${id}/status?new_status=${encodeURIComponent(newStatus)}`, { method: "POST", headers: headers() }).then(handle),

  // Login history & anomaly alerts
  loginHistory: (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return fetch(`${API}/api/admin/login-history${qs ? `?${qs}` : ""}`, { headers: headers() }).then(handle);
  },
  loginAlerts: (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return fetch(`${API}/api/admin/login-alerts${qs ? `?${qs}` : ""}`, { headers: headers() }).then(handle);
  },
  markAlertSeen: (eventId) =>
    fetch(`${API}/api/admin/login-alerts/${eventId}/seen`, { method: "POST", headers: headers() }).then(handle),

  // Registration requests
  listRegistrationRequests: (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return fetch(`${API}/api/admin/registration-requests${qs ? `?${qs}` : ""}`, { headers: headers() }).then(handle);
  },
  countPendingRequests: () =>
    fetch(`${API}/api/admin/registration-requests/count-pending`, { headers: headers() }).then(handle),
  approveRegistrationRequest: (id, data) =>
    fetch(`${API}/api/admin/registration-requests/${id}/approve`, { method: "POST", headers: headers(), body: JSON.stringify(data || {}) }).then(handle),
  rejectRegistrationRequest: (id, reason) =>
    fetch(`${API}/api/admin/registration-requests/${id}/reject`, { method: "POST", headers: headers(), body: JSON.stringify({ reason }) }).then(handle),

  // Simulations per client
  simulationsByClient: (clientId) =>
    fetch(`${API}/api/staff/simulator/by-client/${clientId}`, { headers: headers() }).then(handle),
  deleteSimulation: (simId) =>
    fetch(`${API}/api/staff/simulator/${simId}`, { method: "DELETE", headers: headers() }).then(handle),

  // Contracts
  createContract: (data) =>
    fetch(`${API}/api/staff/contracts`, { method: "POST", headers: headers(), body: JSON.stringify(data) }).then(handle),
  contractsByClient: (clientId) =>
    fetch(`${API}/api/staff/contracts/by-client/${clientId}`, { headers: headers() }).then(handle),
  salesContractsByClient: (clientId) =>
    fetch(`${API}/api/staff/sales-contracts/by-client/${clientId}`, { headers: headers() }).then(handle),
  deleteContract: (id) =>
    fetch(`${API}/api/staff/contracts/${id}`, { method: "DELETE", headers: headers() }).then(handle),
  listAllContracts: (params = {}) => {
    const qs = new URLSearchParams(
      Object.entries(params).filter(([_, v]) => v !== "" && v != null)
    ).toString();
    return fetch(`${API}/api/admin/contracts${qs ? `?${qs}` : ""}`, { headers: headers() }).then(handle);
  },
  contractsStats: () =>
    fetch(`${API}/api/admin/contracts/stats`, { headers: headers() }).then(handle),
  salesPendingSignatures: () =>
    fetch(`${API}/api/admin/sales-contracts/pending-signatures`, { headers: headers() }).then(handle),

  // Canoni noleggio — scadenzario pagamenti
  canoniList: (filter = "all") =>
    fetch(`${API}/api/admin/canoni?filter=${filter}`, { headers: headers() }).then(handle),
  canoniSummary: () =>
    fetch(`${API}/api/admin/canoni/summary`, { headers: headers() }).then(handle),
  canoneMarkPaid: (id) =>
    fetch(`${API}/api/admin/canoni/${id}/mark-paid`, { method: "POST", headers: headers() }).then(handle),
  canoneMarkUnpaid: (id) =>
    fetch(`${API}/api/admin/canoni/${id}/mark-unpaid`, { method: "POST", headers: headers() }).then(handle),
};
